import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, Info } from 'lucide-react';
import { MapboxAddressInput, AddressData } from '@/components/MapboxAddressInput';
import HolidaySelector from '@/components/displays/HolidaySelector';
import { HolidayContentEditor } from '@/components/displays/edit/HolidayContentEditor';
import { toast } from 'sonner';
import { HolidayValue, getHolidayOption } from '@/constants/holidays';
import { supabase } from '@/integrations/supabase/client';

interface ViewDisplay {
  id: string;
  name: string;
  description: string;
  image_url: string;
  photos: string[];
  location: string;
  latitude: number | null;
  longitude: number | null;
  display_type: string;
  holidays: string[];
  city?: string;
  state?: string;
}

interface DisplayEditFormTabbedProps {
  display: ViewDisplay;
  onSave: (display: ViewDisplay) => void;
  onCancel: () => void;
  onChange: (display: ViewDisplay | null) => void;
  onUnsavedChangesChange?: (hasUnsavedChanges: boolean) => void;
}

/**
 * DisplayEditFormTabbed - Modern tabbed interface for multi-holiday displays
 *
 * Structure:
 * - Base Info Tab: name, address, display_type, holiday selector (shared)
 * - One tab per selected holiday: uses HolidayContentEditor for holiday-specific content
 *
 * Benefits:
 * - Clean separation of shared vs holiday-specific content
 * - Scalable to any number of holidays
 * - Each holiday gets dedicated editor space
 */
const DisplayEditFormTabbed: React.FC<DisplayEditFormTabbedProps> = ({
  display,
  onSave,
  onCancel,
  onChange,
  onUnsavedChangesChange,
}) => {
  const [activeTab, setActiveTab] = useState<string>('base');
  const [hasChanges, setHasChanges] = useState(false);
  const [isSavingBaseInfo, setIsSavingBaseInfo] = useState(false);

  // Detect date-based default tab (for auto-selecting current holiday)
  useEffect(() => {
    const getDefaultHolidayTab = () => {
      if (display.holidays.length === 0) return 'base';

      const today = new Date();
      const month = today.getMonth(); // 0-11

      // October: Halloween
      if (month === 9 && display.holidays.includes('Halloween')) return 'Halloween';
      // Nov-Dec: Christmas
      if ([10, 11].includes(month) && display.holidays.includes('Christmas')) return 'Christmas';
      // Feb: Valentine's Day
      if (month === 1 && display.holidays.some(h => h.includes('Valentine'))) {
        return display.holidays.find(h => h.includes('Valentine')) || display.holidays[0];
      }
      // July: 4th of July
      if (month === 6 && display.holidays.some(h => h.includes('July'))) {
        return display.holidays.find(h => h.includes('July')) || display.holidays[0];
      }
      // Easter (March-April)
      if ([2, 3].includes(month) && display.holidays.includes('Easter')) return 'Easter';

      // Default to first holiday
      return display.holidays[0];
    };

    // Only auto-select on first load if we have holidays
    if (display.holidays.length > 0 && activeTab === 'base') {
      setActiveTab(getDefaultHolidayTab());
    }
  }, [display.holidays]);

  const handleChange = (field: keyof ViewDisplay, value: any) => {
    setHasChanges(true);
    onChange({
      ...display,
      [field]: value,
    });
  };

  const handleAddressChange = (value: string) => {
    handleChange('location', value);
  };

  const handleAddressSelect = (address: AddressData) => {
    setHasChanges(true);
    onChange({
      ...display,
      latitude: address.latitude || null,
      longitude: address.longitude || null,
      location: address.fullAddress,
      city: address.city,
      state: address.state
    });
  };

  const handleSaveBaseInfo = async () => {
    try {
      setIsSavingBaseInfo(true);

      // Update basic display info
      const { error } = await supabase
        .from('displays')
        .update({
          name: display.name,
          location: display.location,
          latitude: display.latitude,
          longitude: display.longitude,
          city: display.city,
          state: display.state,
          display_type: display.display_type,
          holidays: display.holidays,
          updated_at: new Date().toISOString()
        })
        .eq('id', parseInt(display.id));

      if (error) throw error;

      toast.success('Base information saved successfully');
      setHasChanges(false);
      await onSave(display);
    } catch (error) {
      console.error('Error saving base info:', error);
      toast.error('Failed to save base information');
    } finally {
      setIsSavingBaseInfo(false);
    }
  };

  const renderHolidayTab = (holiday: string) => {
    const holidayOption = getHolidayOption(holiday);
    const icon = holidayOption?.icon || '✨';
    const label = holidayOption?.label || holiday;

    return (
      <TabsTrigger key={holiday} value={holiday} className="gap-1">
        <span>{icon}</span>
        <span>{label}</span>
      </TabsTrigger>
    );
  };

  return (
    <Card className="p-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full gap-2" style={{ gridTemplateColumns: `repeat(${display.holidays.length + 1}, minmax(0, 1fr))` }}>
          <TabsTrigger value="base" className="gap-1">
            <Info className="w-4 h-4" />
            <span>Base Info</span>
          </TabsTrigger>
          {display.holidays.map(renderHolidayTab)}
        </TabsList>

        {/* Base Info Tab */}
        <TabsContent value="base" className="space-y-6 mt-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Display Name</Label>
              <Input
                id="name"
                value={display.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Enter display name"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The name visitors will see on the map
              </p>
            </div>

            <div>
              <Label htmlFor="location">Address</Label>
              <MapboxAddressInput
                value={display.location}
                onChange={handleAddressChange}
                onAddressSelect={handleAddressSelect}
                placeholder="Start typing your address..."
                className="w-full"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Start typing to see address suggestions
              </p>
            </div>

            <div>
              <Label htmlFor="display_type">Display Type</Label>
              <Input
                id="display_type"
                value={display.display_type}
                onChange={(e) => handleChange('display_type', e.target.value)}
                placeholder="e.g., Animated, Static, Walkthrough"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Describe your display style
              </p>
            </div>

            <div>
              <Label htmlFor="holidays">Holidays Celebrated</Label>
              <HolidaySelector
                value={display.holidays}
                onChange={(holidays) => handleChange('holidays', holidays)}
                mode="checkbox"
                layout="grid"
                showIcons={true}
                showSections={true}
              />
              <p className="text-sm text-muted-foreground mt-1">
                Select all holidays your display celebrates. Each holiday will get its own tab for specific content.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSaveBaseInfo}
              disabled={isSavingBaseInfo || !hasChanges}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Base Info
            </Button>
          </div>
        </TabsContent>

        {/* Holiday-Specific Content Tabs */}
        {display.holidays.map((holiday) => (
          <TabsContent key={holiday} value={holiday} className="mt-6">
            <HolidayContentEditor
              displayId={parseInt(display.id)}
              holiday={holiday as HolidayValue}
              city={display.city}
              state={display.state}
              onContentChange={() => {
                // Refresh parent if needed
                onSave(display);
              }}
            />
          </TabsContent>
        ))}
      </Tabs>

      {display.holidays.length === 0 && activeTab === 'base' && (
        <div className="mt-4 p-4 bg-muted rounded-lg text-center">
          <p className="text-sm text-muted-foreground">
            👆 Select at least one holiday above to add photos, videos, songs, and schedules specific to that holiday
          </p>
        </div>
      )}
    </Card>
  );
};

export default DisplayEditFormTabbed;
